import { supabase } from '@/lib/supabase'
import type { MealPlan, PlannedMeal } from '@/types/meal-plan'
import { requireSessionUser } from './api-utils'

const attachRecipesToPlannedMeals = async <T extends PlannedMeal>(plannedMeals: T[]): Promise<T[]> => {
  const recipeIds = [...new Set(plannedMeals.map((meal) => meal.recipe_id).filter((id): id is string => Boolean(id)))]

  if (!recipeIds.length) {
    return plannedMeals.map((meal) => ({ ...meal, recipe: undefined }))
  }

  const { data, error } = await supabase.from('recipes').select('*').in('id', recipeIds)

  if (error) {
    return plannedMeals.map((meal) => ({ ...meal, recipe: undefined }))
  }

  const recipesById = new Map((data ?? []).map((recipe) => [recipe.id, recipe]))

  return plannedMeals.map((meal) => ({
    ...meal,
    recipe: meal.recipe_id ? recipesById.get(meal.recipe_id) ?? undefined : undefined,
  }))
}

const getMealPlans = async (householdId?: string): Promise<MealPlan[]> => {
  let query = supabase
    .from('meal_plans')
    .select('*')
    .eq('is_template', false)
    .order('start_date', { ascending: false })

  if (householdId) {
    query = query.eq('household_id', householdId)
  }

  const { data, error } = await query
  if (error) {
    throw error
  }

  return data ?? []
}

const getMealPlan = async (id: string): Promise<MealPlan> => {
  const { data, error } = await supabase.from('meal_plans').select('*').eq('id', id).single()
  if (error) {
    throw error
  }
  return data
}

const createMealPlan = async (mealPlan: Partial<MealPlan>): Promise<MealPlan> => {
  const user = await requireSessionUser()
  const { data, error } = await supabase
    .from('meal_plans')
    .insert({
      household_id: mealPlan.household_id,
      created_by: user.id,
      name: mealPlan.name,
      description: mealPlan.description ?? null,
      plan_type: mealPlan.plan_type ?? 'weekly',
      start_date: mealPlan.start_date,
      end_date: mealPlan.end_date,
      is_template: mealPlan.is_template ?? false,
      template_name: mealPlan.template_name ?? null,
      is_active: mealPlan.is_active ?? true,
    })
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const updateMealPlan = async (id: string, updates: Partial<MealPlan>): Promise<MealPlan> => {
  const { data, error } = await supabase
    .from('meal_plans')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const deleteMealPlan = async (id: string): Promise<void> => {
  const { error } = await supabase.from('meal_plans').delete().eq('id', id)
  if (error) {
    throw error
  }
}

const getPlannedMeals = async (mealPlanId: string, startDate?: string, endDate?: string): Promise<PlannedMeal[]> => {
  let query = supabase
    .from('planned_meals')
    .select('*')
    .eq('meal_plan_id', mealPlanId)
    .order('planned_date', { ascending: true })

  if (startDate) {
    query = query.gte('planned_date', startDate)
  }
  if (endDate) {
    query = query.lte('planned_date', endDate)
  }

  const { data, error } = await query
  if (error) {
    throw error
  }

  return attachRecipesToPlannedMeals(data ?? [])
}

const createPlannedMeal = async (mealPlanId: string, plannedMeal: Partial<PlannedMeal>): Promise<PlannedMeal> => {
  const { data, error } = await supabase
    .from('planned_meals')
    .insert({
      meal_plan_id: mealPlanId,
      recipe_id: plannedMeal.recipe_id ?? null,
      planned_date: plannedMeal.planned_date,
      meal_type: plannedMeal.meal_type,
      planned_servings: plannedMeal.planned_servings ?? 1,
      notes: plannedMeal.notes ?? null,
      assigned_to: plannedMeal.assigned_to ?? null,
    })
    .select('*')
    .single()

  if (error) {
    throw error
  }

  return (await attachRecipesToPlannedMeals([data]))[0]
}

const updatePlannedMeal = async (id: string, updates: Partial<PlannedMeal>): Promise<PlannedMeal> => {
  const { data, error } = await supabase
    .from('planned_meals')
    .update(updates)
    .eq('id', id)
    .select('*')
    .single()

  if (error) {
    throw error
  }

  return (await attachRecipesToPlannedMeals([data]))[0]
}

const deletePlannedMeal = async (id: string): Promise<void> => {
  const { error } = await supabase.from('planned_meals').delete().eq('id', id)
  if (error) {
    throw error
  }
}

export const mealPlanApi = {
  getMealPlans,
  getMealPlan,
  createMealPlan,
  updateMealPlan,
  deleteMealPlan,
  getPlannedMeals,
  createPlannedMeal,
  updatePlannedMeal,
  deletePlannedMeal,
}
